import React, { useEffect, useRef } from 'react';
import {
  DesktopDatePicker,
  DesktopDatePickerSlotProps,
} from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { Controller, Control, FieldValues } from 'react-hook-form';

interface ControlledDesktopDatePickerInputProps<T extends FieldValues> {
  control: Control<T>;
  label: string;
  name: string;
  controllerProps?: any;
  inputsProps?: Partial<React.ComponentProps<typeof DesktopDatePicker>>;
  slotProps?: Partial<DesktopDatePickerSlotProps<false>>;
  errors: any;
  isDirty: boolean;
  displayFormat?: string;
  onEnterKeyPress?: () => void;
  autofocus?: boolean;
  resetKey?: string | number;
  disabled?: boolean;
}

const ControlledDesktopDatePickerInput = <T extends FieldValues>(
  props: ControlledDesktopDatePickerInputProps<T>
) => {
  const {
    displayFormat = 'dd/MM/yyyy',
    name,
    label,
    slotProps,
    isDirty,
    errors,
    onEnterKeyPress,
  } = props;

  const handleOnEnterKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Enter' && props.onEnterKeyPress) {
      props.onEnterKeyPress();
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current != null && props.autofocus) {
      inputRef.current.focus();
    }
  }, [props.autofocus, props.resetKey]);

  return (
    <Controller
      {...props.controllerProps}
      control={props.control}
      name={props.name}
      render={({ field: { onChange, value } }) => {
        return (
          <LocalizationProvider adapterLocale="es" dateAdapter={AdapterLuxon}>
            <DesktopDatePicker
              inputRef={inputRef}
              {...props.inputsProps}
              format={displayFormat}
              disabled={props.disabled}
              slotProps={{
                ...slotProps,
                textField: {
                  fullWidth: true,
                  helperText: isDirty && errors && errors.message,
                  error: isDirty && Boolean(errors),
                  onKeyDown: onEnterKeyPress
                    ? handleOnEnterKeyPress
                    : undefined,
                  ...slotProps?.textField,
                },
              }}
              label={label}
              name={name}
              value={value}
              onChange={onChange}
            />
          </LocalizationProvider>
        );
      }}
    />
  );
};

export default ControlledDesktopDatePickerInput;
